import { Link } from "react-router";

import { Button } from "../ui/button";
import { cn } from "../ui/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Icon } from "../ui/icon";
import type { BellState } from "./shell-counts";

// The bell of the top bar. A dot shows while something needs the staff member (a source of the attention summary that needs action and is
// not empty). The menu lists those sources, each a link to the place that lists them: the same sources as "Needs action now" on Home.

/** What a screen reader hears beside "Notifications" while the dot shows. */
function needText(lists: number): string {
  if (lists === 0) return "some queues may need you";
  return `${lists} ${lists === 1 ? "list needs" : "lists need"} you`;
}

export function NotificationBell({ state }: Readonly<{ state: BellState }>) {
  const waiting = state.entries.length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-xl">
          <Icon name="notification-01" size="lg" />
          {state.dot ? (
            <span
              aria-hidden="true"
              data-testid="bell-dot"
              className="absolute top-2.5 right-2.5 size-2 rounded-full border-2 border-background bg-brass box-content compact:top-1 compact:right-1"
            />
          ) : null}
          <span className="sr-only">
            Notifications
            {state.dot ? `, ${needText(waiting)}` : ""}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-w-[calc(100vw-1.5rem)]">
        <DropdownMenuLabel className="text-sm text-foreground">Needs you now</DropdownMenuLabel>
        {state.unreadable ? (
          <p className="m-0 px-2.5 py-2 text-sm text-muted-foreground">
            The list could not be read. Open Home to try again.
          </p>
        ) : null}
        {!state.unreadable && waiting === 0 ? (
          <p className="m-0 px-2.5 py-2 text-sm text-muted-foreground">
            {state.blind
              ? "Nothing found. Some queues could not be read."
              : "Nothing needs you right now."}
          </p>
        ) : null}
        {state.entries.map((entry) => (
          <DropdownMenuItem key={entry.key} asChild>
            <Link
              to={entry.to}
              className={cn("justify-between no-underline", entry.urgent && "font-semibold")}
            >
              <span>{entry.text}</span>
              {entry.urgent ? (
                <span className="text-xs font-semibold text-destructive">Urgent</span>
              ) : null}
            </Link>
          </DropdownMenuItem>
        ))}
        {state.blind && waiting > 0 ? (
          <p className="m-0 px-2.5 py-2 text-xs text-muted-foreground">
            Some queues could not be read, so this list may be short.
          </p>
        ) : null}
        <DropdownMenuItem asChild>
          <Link to="/home" className="mt-1 border-t border-hairline text-link no-underline">
            Open Home
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
